/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Injectable, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ArcBaseService } from '@trustarc/primeng-components';
import * as Comlink from 'comlink';
import { environment } from 'src/environments/environment';
import { PlatformNavService } from './platform-nav.service';

interface ArcUiBridge {
  otpRequest(callback: any, type: any, code: any): any;
}

@Injectable()
export class ArcService extends ArcBaseService {
  private router: Router | null = null;
  private platformNavService: PlatformNavService | null = null;
  private arcUiBridge: ArcUiBridge | null = null;

  get arcUrl(): string {
    return environment.arcUrl;
  }

  get arcSubdirectory(): string {
    return environment.arcSubdirectory;
  }

  get arcEnabled(): boolean {
    return environment.arcEnabled;
  }

  init(readyCallback: () => void): void {
    if (this.fromArc) {
      this.bindToMessageListener(readyCallback);
      this.addGlobalStyle();
    } else if (this.arcEnabled) {
      this.redirectToArc();
    }
  }

  redirectToArc(): boolean {
    if (
      this.document.location.origin !== this.arcUrl &&
      !this.document.location.origin.startsWith('http://localhost')
    ) {
      this.document.location.href = `${this.arcUrl}/${this.arcSubdirectory}${this.document.location.pathname}${this.document.location.search}${this.document.location.hash}`;
      return true;
    }

    return false;
  }

  initListeners(
    router: Router,
    platformNavService: PlatformNavService,
    renderer: Renderer2,
  ): void {
    this.router = router;
    this.platformNavService = platformNavService;

    if (this.fromArc) {
      this.bindToRouterEvents();
      this.observeBackdrop(renderer);
    }
  }

  private bindToMessageListener(readyCallback: () => void) {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('message', (event) => {
        if (!event.data || !event.data.type) {
          return;
        }

        switch (event.data.type) {
          case ArcBaseService.arcAuthKey:
            {
              (window as any).trustArcPlatformNavigation = {
                ...JSON.parse(event.data.trustArcPlatformNavigation),
                on: () => {},
                selectMenu: () => {},
                breadcrumbs: {
                  create: () => {},
                  add: () => {},
                  back: () => {},
                  clear: () => {},
                  current: () => {},
                  item: () => {},
                  select: () => {},
                  setRootItem: () => {},
                  removeRootItem: () => {},
                  on: () => {},
                  items: () => [],
                },
              };
              (window as any).truste = JSON.parse(event.data.truste);
              (window as any).trusteapi = JSON.parse(event.data.trusteapi);
              (window as any).trusteapi.user.getUserInfo = (
                callback: (userinfo: any) => void,
              ) => {
                fetch(`${environment.baseUrl}/oauth/userinfo`, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${environment.token}`,
                  },
                })
                  .then((response) => response.json())
                  .then((userInfo: any) => {
                    callback(userInfo);
                  });
              };
              this.connectArcUiBridge();
              this.ngZone.run(() => readyCallback());
            }
            break;

          case ArcBaseService.arcNavigateKey:
            this.ngZone.run(() => {
              if (event.data.path.startsWith('/#/')) {
                event.data.path = event.data.path.substring(2);
              }

              this.platformNavService.setNext(event.data.path);
            });
            break;
        }

        this.handleAdditionalArcMessages(event);
      });

      this.postAuthMessage();
    });
  }

  private bindToRouterEvents() {
    this.router?.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.postSubAppUrlChangeMessage(event.urlAfterRedirects);
      }
    });
  }

  private addGlobalStyle() {
    const styleSheet = this.document.createElement('style');
    styleSheet.innerHTML = `
  ta-platform-navigation {
    display: none !important;
  }

  #main-container {
    display: block !important;
    padding: 0 !important;
  }

  .main-content {
    height: 100vh !important;
  }

  .main-side-nav-container {
    height: calc(100vh - 120px) !important;
  }
  `;
    this.document.head.appendChild(styleSheet);
  }

  private connectArcUiBridge() {
    const endpoint = Comlink.windowEndpoint(window.parent, window, '*');
    this.arcUiBridge = Comlink.wrap<ArcUiBridge>(endpoint);
    (window as any).trusteapi.auth.otp.request = (
      callback: any,
      type: any,
      code: any,
    ) => {
      const cbProxy = Comlink.proxy(callback);
      return this.arcUiBridge.otpRequest(cbProxy, type, code);
    };
  }
}
